import ModuleLandingPage from "../../Components/ModuleLandingPage";
import { getNavigationRoutes } from "../../Config/routes";
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  ListItem,
  ListItemIcon,
  MenuItem,
  Tooltip,
  Typography,
} from "@material-ui/core";
import Grid from "@material-ui/core/Grid";

import TextField from "@material-ui/core/TextField";

import ChatIcon from "@material-ui/icons/Chat";
import InfoIcon from "@material-ui/icons/Info";
import NotificationsIcon from "@material-ui/icons/Notifications";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import SortableTable from "../../Components/SortableTable";
import Title from "../../Components/Title";

import { useTranslation } from "react-i18next";
import {
  ECABIN,
  findValueInText,
  formatTimeWithTZOffset,
  getInvoiceIssuerData,
  getInvoiceIssuerOptions,
  getInvoiceIssuers,
  getToken,
  getUser,
  invoiceIssuerMatchesValue,
  paymentStates,
  requestErrorHandler,
  searchMatchingList,
} from "../../Utils/Common";
import EnvSettings from "../../Utils/EnvSettings";
import EditOrder from "./EditOrder";
import NewOrder from "./NewOrder";

const server = EnvSettings.server;
const DEFAULT_RESUPPLY_FILTER_STATES = [
  "invoiced",
  "deposit",
  "completed",
  "transit",
];

const getInitialFilterStates = (orderId) =>
  orderId ? paymentStates.map((m) => m.value) : DEFAULT_RESUPPLY_FILTER_STATES;

const mergeOrdersById = (currentOrders, newOrders) => {
  const ordersById = new Map();
  [...(currentOrders || []), ...(newOrders || [])].forEach((order) => {
    if (order?.id) {
      ordersById.set(order.id, order);
    }
  });
  return Array.from(ordersById.values());
};

const getFilterDateTime = (date, endOfDay) => {
  if (!date) {
    return undefined;
  }
  const parsedDate = new Date(
    date + (endOfDay ? "T23:59:59.999" : "T00:00:00"),
  );
  const time = parsedDate.getTime();
  return Number.isNaN(time) ? undefined : time;
};

const isDateInRange = (dateValue, dateFrom, dateTo) => {
  if (!dateFrom && !dateTo) {
    return true;
  }
  if (!dateValue) {
    return false;
  }
  const dateTime = new Date(dateValue).getTime();
  if (Number.isNaN(dateTime)) {
    return false;
  }
  const fromTime = getFilterDateTime(dateFrom);
  const toTime = getFilterDateTime(dateTo, true);
  return (
    (fromTime === undefined || dateTime >= fromTime) &&
    (toTime === undefined || dateTime <= toTime)
  );
};

const getOrderDisplayUpdatedAt = (order) => {
  if (order?.Notes && order.Notes.length) {
    const latestNote = order.Notes.reduce((prev, current) =>
      prev && prev.id > current.id ? prev : current,
    );
    return latestNote?.updatedAt;
  }
  return order?.activityUpdatedAt || order?.updatedAt;
};

export default function ResupplyOrders(props) {
  const order_id = props?.computedMatch?.params?.order_id;

  const [searchList, setSearchList] = useState();
  const [orderList, setOrderList] = useState();
  const [searchValue, setSearchValue] = useState(() => order_id || "");
  const [orderFilters, setOrderFilters] = useState({});
  const [expandOrderItems, setExpandOrderItems] = useState(false);
  const [showAdditionalFilters, setShowAdditionalFilters] = useState(false);
  const [filterStates, setFilterStates] = useState(() =>
    getInitialFilterStates(order_id),
  );
  const [fetchedFilterStates, setFetchedFilterStates] = useState([]);
  const [loadingFilterStates, setLoadingFilterStates] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const [invoiceIssuers, setInvoiceIssuers] = useState([]);
  const dispatch = useDispatch();
  useEffect(() => {
    getInvoiceIssuers(setInvoiceIssuers, dispatch);
  }, [dispatch]);

  const editBoxRef = useRef(null);

  const updateOrderFilter = (key, value) => {
    setOrderFilters((currentFilters) => ({
      ...currentFilters,
      [key]: value || undefined,
    }));
  };

  const executeScroll = () => {
    editBoxRef.current.scrollIntoView({ behavior: "smooth" });
  };

  const showOrder = (dev) => {
    setSelectedOrder();
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
        view: "detail",
        order_id: dev.id,
      }),
    };
    fetch(server + "/get_resupply_orders", requestOptions)
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (!result.error) {
          // Hack for refreshing the selected device component
          setTimeout(() => {
            setSelectedOrder(result.orders?.[0] || dev);
            if (editBoxRef && editBoxRef.current) {
              executeScroll();
            }
          }, 1);
        }
      })
      .catch(() => {
        setTimeout(() => {
          setSelectedOrder(dev);
          if (editBoxRef && editBoxRef.current) {
            executeScroll();
          }
        }, 1);
      });
  };

  const getResupplyOrders = (statesToFetch, replace) => {
    const paymentStatesToFetch = Array.isArray(statesToFetch)
      ? statesToFetch.filter(Boolean)
      : filterStates;
    if (!paymentStatesToFetch.length) {
      if (replace) {
        setOrderList([]);
        setSearchList([]);
        setFetchedFilterStates([]);
      }
      return;
    }
    setLoading(true);
    setLoadingFilterStates((currentStates) => [
      ...new Set([...currentStates, ...paymentStatesToFetch]),
    ]);
    if (replace) {
      setSearchList();
      setSelectedOrder();
    }
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
        view: "list",
        paymentStates: paymentStatesToFetch,
      }),
    };
    fetch(server + "/get_resupply_orders", requestOptions)
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (!result.error) {
          const fetchedOrders = result.orders || [];
          setOrderList((currentOrders) =>
            replace
              ? fetchedOrders
              : mergeOrdersById(currentOrders, fetchedOrders),
          );
          setFetchedFilterStates((currentStates) =>
            replace
              ? paymentStatesToFetch
              : [...new Set([...currentStates, ...paymentStatesToFetch])],
          );
        }
        setLoading(false);
        setLoadingFilterStates((currentStates) =>
          currentStates.filter(
            (state) => !paymentStatesToFetch.includes(state),
          ),
        );
      })
      .catch((e) => {
        setLoading(false);
        setLoadingFilterStates((currentStates) =>
          currentStates.filter(
            (state) => !paymentStatesToFetch.includes(state),
          ),
        );
      });
  };

  const handleFilterStateChange = (state, selected) => {
    const nextStates = selected
      ? [...new Set([...filterStates, state])]
      : filterStates.filter((filterState) => filterState !== state);
    setFilterStates(nextStates);
    if (selected && !fetchedFilterStates.includes(state)) {
      getResupplyOrders([state], false);
    }
  };

  const filterResults = () => {
    setSearchList(
      orderList &&
        orderList.filter((contact) => {
          return (
            filterStates.find((o) => o === contact.paymentStatus) &&
            searchMatchingList(
              [
                contact.number,
                contact.genericDevices,
                contact.User,
                contact.invoiceIssuer,
                contact.SupplierCompany,
              ],
              searchValue,
            ) &&
            invoiceIssuerMatchesValue(contact.buyer, orderFilters.buyer) &&
            findValueInText(
              contact.SupplierCompany?.name || contact.invoiceIssuer,
              orderFilters.supplier,
            ) &&
            findValueInText(contact.User?.fullName, orderFilters.createdBy) &&
            findValueInText(contact.genericDevices, orderFilters.items) &&
            isDateInRange(
              contact.createdAt,
              orderFilters.createdFrom,
              orderFilters.createdTo,
            ) &&
            isDateInRange(
              getOrderDisplayUpdatedAt(contact),
              orderFilters.updatedFrom,
              orderFilters.updatedTo,
            )
          );
        }),
    );
  };

  useEffect(filterResults, [
    searchValue,
    orderList,
    filterStates,
    orderFilters.buyer,
    orderFilters.supplier,
    orderFilters.createdBy,
    orderFilters.items,
    orderFilters.createdFrom,
    orderFilters.createdTo,
    orderFilters.updatedFrom,
    orderFilters.updatedTo,
    invoiceIssuers,
  ]);

  useEffect(() => {
    getResupplyOrders(getInitialFilterStates(order_id), true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigationLinks = getNavigationRoutes("resupply").map((route) => ({
    href: route.path,
    title: t(route.title),
    icon: route.icon,
  }));

  return (
    <ModuleLandingPage
      title={t("resupplyOrders.header")}
      actions={<NewOrder />}
      navigation={navigationLinks}
      filters={
        <>
          <Grid item xs={12} sm={3}>
            <TextField
              autoFocus
              label={t("search")}
              type="text"
              fullWidth
              variant="outlined"
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value.toLowerCase());
              }}
              ref={editBoxRef}
            />
          </Grid>
          <Grid item container spacing={1} xs={12} alignItems="center">
            <Grid item>
              <Title>{t("quickFilters")}</Title>
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                onClick={() => {
                  setShowAdditionalFilters(!showAdditionalFilters);
                }}
              >
                {showAdditionalFilters ? t("hide") : t("show")}
              </Button>
            </Grid>
          </Grid>
          {showAdditionalFilters ? (
            <>
              <Grid item xs={12} sm={3}>
                <TextField
                  label={t("resupplyOrders.buyer")}
                  variant="outlined"
                  select
                  fullWidth
                  value={orderFilters.buyer || ""}
                  onChange={(e) => {
                    updateOrderFilter("buyer", e.target.value);
                  }}
                >
                  {[
                    { name: "", data: { name: t("select") } },
                    ...getInvoiceIssuerOptions(invoiceIssuers),
                  ].map((m, i) => (
                    <MenuItem key={(m.name || "select") + i} value={m.name}>
                      {m?.data?.name || t("select")}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label={t("resupplyOrders.orderFrom")}
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={orderFilters.supplier || ""}
                  onChange={(e) => {
                    updateOrderFilter("supplier", e.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label={t("orders.createdFrom")}
                  type="date"
                  fullWidth
                  variant="outlined"
                  value={orderFilters.createdFrom || ""}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => {
                    updateOrderFilter("createdFrom", e.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label={t("orders.createdTo")}
                  type="date"
                  fullWidth
                  variant="outlined"
                  value={orderFilters.createdTo || ""}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => {
                    updateOrderFilter("createdTo", e.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label={t("orders.updatedFrom")}
                  type="date"
                  fullWidth
                  variant="outlined"
                  value={orderFilters.updatedFrom || ""}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => {
                    updateOrderFilter("updatedFrom", e.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label={t("orders.updatedTo")}
                  type="date"
                  fullWidth
                  variant="outlined"
                  value={orderFilters.updatedTo || ""}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => {
                    updateOrderFilter("updatedTo", e.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label={t("orders.createdBy")}
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={orderFilters.createdBy || ""}
                  onChange={(e) => {
                    updateOrderFilter("createdBy", e.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label={t("orders.itemsFilter")}
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={orderFilters.items || ""}
                  onChange={(e) => {
                    updateOrderFilter("items", e.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={expandOrderItems}
                      onChange={(e, checked) => {
                        setExpandOrderItems(checked);
                      }}
                    />
                  }
                  label={t("orders.expandItems")}
                />
              </Grid>
            </>
          ) : null}
          {searchList ? (
            <Grid container item xs={12}>
              <Grid item xs={11}>
                <Typography>{t("orders.paymentStatus")}</Typography>
                {paymentStates
                  .filter((f) => f.value !== "waiting")
                  .map((o) => {
                    const isStateLoading = loadingFilterStates.includes(
                      o.value,
                    );
                    return (
                      <FormControlLabel
                        key={o.label}
                        control={
                          <Checkbox
                            color={o.color || "primary"}
                            disabled={isStateLoading}
                            style={{ color: o.color || "" }}
                            onChange={(e, v) => {
                              handleFilterStateChange(o.value, v);
                            }}
                            checked={!!filterStates.find((f) => f === o.value)}
                          />
                        }
                        label={
                          <>
                            {t("paymentStates." + o.value)}
                            {isStateLoading ? (
                              <CircularProgress
                                size={12}
                                style={{ marginLeft: 6 }}
                              />
                            ) : null}
                          </>
                        }
                      />
                    );
                  })}
              </Grid>
              <Grid item xs={1}></Grid>
            </Grid>
          ) : null}
        </>
      }
      dialogs={
        <>
          <EditOrder selectedOrder={selectedOrder} />
        </>
      }
    >
      {searchList ? (
        <>
          <SortableTable
            tableLayout={"fixed"}
            downloadButton={true}
            header={t("resupplyOrders.header")}
            headCells={[
              {
                id: "Notes",
                label: "",
                width: 32,
                hideFromCsv: true,
              },
              {
                id: "EmailListResupplyOrders",
                label: "",
                width: 32,
                hideFromCsv: true,
              },
              {
                id: "updatedAt",
                numeric: false,
                disablePadding: false,
                label: t("updatedAt"),
              },
              {
                id: "createdAt",
                numeric: false,
                disablePadding: false,
                label: t("createdAt"),
              },
              {
                id: "User.fullName",
                numeric: false,
                disablePadding: false,
                label: t("createdBy"),
              },
              {
                id: "buyer",
                numeric: false,
                disablePadding: false,
                label: t("resupplyOrders.buyer"),
              },
              {
                id: "invoiceIssuer",
                numeric: false,
                disablePadding: false,
                label: t("orders.invoiceIssuer"),
              },
              {
                id: "number",
                numeric: false,
                disablePadding: false,
                label: t("orders.orderNumber"),
              },
              {
                id: "paymentStatus",
                numeric: false,
                disablePadding: false,
                label: t("orders.paymentStatus"),
              },
              {
                id: "genericDevices",
                numeric: false,
                disablePadding: false,
                label: t("items"),
              },
              {
                id: "totalPrice",
                numeric: false,
                disablePadding: false,
                label: t("orders.totalPrice"),
              },
            ]}
            // disablePagination: true,
            defaultSortRow={"updatedAt"}
            handleRowClick={showOrder}
            getRowStyle={(data) => {
              var obj = { cursor: "pointer" };
              var p = paymentStates.find((f) => data.paymentStatus === f.value);
              if (p && p.color) {
                return { ...obj, backgroundColor: p.color };
              }
              return obj;
            }}
            specialDataFormatting={[
              {
                id: "updatedAt",
                format: (data, d, dd) => {
                  if (dd.Notes && dd.Notes.length) {
                    const max = dd.Notes.reduce((prev, current) =>
                      prev && prev.id > current.id ? prev : current,
                    );
                    return formatTimeWithTZOffset(max.updatedAt, ECABIN);
                  }
                  if (dd.activityUpdatedAt) {
                    return formatTimeWithTZOffset(dd.activityUpdatedAt, ECABIN);
                  }
                  return formatTimeWithTZOffset(data, ECABIN);
                },
              },
              {
                id: "invoiceIssuer",
                format: (data, d, dd) => {
                  if (dd.SupplierCompany) {
                    return dd.SupplierCompany.name;
                  }
                  return data;
                },
              },
              {
                id: "Notes",
                format: (data) => {
                  if (data?.find((f) => f.User)) {
                    return (
                      <Tooltip title={t("activeComments")}>
                        <ChatIcon color="primary" style={{ fontSize: 20 }} />
                      </Tooltip>
                    );
                  }
                  return "";
                },
              },
              {
                id: "EmailListResupplyOrders",
                format: (data, d, dd) => {
                  if (
                    dd.isSubscribed ||
                    (data &&
                      data.find(
                        (f) =>
                          f &&
                          f.EmailList?.EmailListUsers.find(
                            (u) => u.User.id === getUser().userId,
                          ),
                      )) ||
                    dd.UserId === getUser().userId
                  ) {
                    return (
                      <Tooltip title={t("tagged")}>
                        <NotificationsIcon
                          color="primary"
                          style={{ fontSize: 20 }}
                        />
                      </Tooltip>
                    );
                  }
                  return "";
                },
              },
              {
                id: "createdAt",
                format: (data) => {
                  return formatTimeWithTZOffset(data, ECABIN);
                },
              },
              {
                id: "genericDevices",
                format: (data, dataFormat) => {
                  const items = Array.isArray(data) ? data : [];
                  const formatItemsForCsv = (orderItems) =>
                    orderItems
                      .map((v) =>
                        [v.count, "x", v.name, v.sku ? "(" + v.sku + ")" : ""]
                          .filter(Boolean)
                          .join(" "),
                      )
                      .join(" | ");
                  if (dataFormat === "json") {
                    return formatItemsForCsv(items);
                  }
                  const renderItems = (orderItems, highlight) => (
                    <>
                      {orderItems.map((v, i) => (
                        <Typography
                          key={(v.sku || v.name || "item") + i}
                          style={{
                            ...(highlight ? { backgroundColor: "yellow" } : {}),
                            fontSize: 15,
                          }}
                        >
                          {v.count + " x " + v.name}
                        </Typography>
                      ))}
                    </>
                  );
                  var x;
                  const itemFilterValue = orderFilters.items || searchValue;
                  if (itemFilterValue && itemFilterValue !== "") {
                    x = items.filter(
                      (f) =>
                        (f.name || "")
                          .toLowerCase()
                          .includes(itemFilterValue.toLowerCase()) ||
                        (f.sku || "")
                          .toLowerCase()
                          .includes(itemFilterValue.toLowerCase()),
                    );
                    if (x.length) {
                      return renderItems(x, true);
                    }
                  }
                  if (items.length > 1) {
                    if (expandOrderItems) {
                      return renderItems(items);
                    }
                    x = 0;
                    items.forEach((d) => (x = x + d.count));
                    return t("count") + ":" + x;
                  }
                  var a = "";
                  items.forEach((v) => (a = v.count + " x " + v.name));
                  return a;
                },
              },
              {
                id: "paymentStatus",
                formatReturnsElement: true,
                format: (data, e, v) => {
                  if (e === "json") {
                    return t(
                      "paymentStates." +
                        paymentStates.find((o) => o.value === data).value,
                    );
                  }
                  return (
                    <ListItem>
                      {t(
                        "paymentStates." +
                          paymentStates.find((o) => o.value === data).value,
                      )}
                      {data === "transit" && v.multipleShipments ? (
                        <Tooltip
                          title={t("orders.multipleShipments")}
                          placement="top"
                        >
                          <ListItemIcon size="small">
                            <InfoIcon
                              style={{ verticalAlign: "middle" }}
                              fontSize="small"
                            />
                          </ListItemIcon>
                        </Tooltip>
                      ) : null}
                    </ListItem>
                  );
                },
              },
              {
                id: "buyer",
                format: (data) => {
                  if (!data) {
                    return data;
                  }
                  return (
                    getInvoiceIssuerData(invoiceIssuers, data)?.name || data
                  );
                },
              },
            ]}
            // handleScrollClick: showDeviceNewTab,
            tableData={searchList}
            rowsPerPage={50}
          />
        </>
      ) : loading ? (
        <CircularProgress />
      ) : undefined}
    </ModuleLandingPage>
  );
}
